import { FrameSharingRow } from "@app/components/assistant/conversation/interactive_content/frame/FrameSharingRow";
import { Section } from "@app/components/assistant/conversation/interactive_content/frame/ShareFrameSection";
import { useAwaitableDialog } from "@app/hooks/useAwaitableDialog";
import { getActiveLocale } from "@app/lib/i18n/active_locale";
import { compareStrings, formatTimeDistance } from "@app/lib/i18n/format";
import { MAX_EMAILS_OR_DOMAINS_PER_INVITE } from "@app/types/files";
import type {
  FileSharingGrantType,
  SharingGrantsResponse,
} from "@app/types/sharing_grants";
import {
  addSharingGrantsSchema,
  sharingDomainSchema,
  sharingEmailSchema,
} from "@app/types/sharing_grants";
import { Button, Globe01, Input, ListGroup, Spinner } from "@dust-tt/sparkle";
import { zodResolver } from "@hookform/resolvers/zod";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useId, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { fromError } from "zod-validation-error";

function getRecipientsSchema(t: (descriptor: MessageDescriptor) => string) {
  const maxRecipients = MAX_EMAILS_OR_DOMAINS_PER_INVITE;
  return z
    .string()
    .transform((raw, ctx) => {
      const recipients = raw
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);
      if (recipients.length === 0 || recipients.length > maxRecipients) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(
            msg`Add between 1 and ${maxRecipients} email addresses or domains.`
          ),
        });
        return z.NEVER;
      }

      const emails: string[] = [];
      const domains: string[] = [];
      for (const recipient of recipients) {
        const email = sharingEmailSchema.safeParse(recipient);
        if (email.success) {
          emails.push(email.data);
          continue;
        }
        const domain = sharingDomainSchema.safeParse(recipient);
        if (domain.success) {
          domains.push(domain.data);
          continue;
        }
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: fromError(domain.error, {
            prefix: `"${recipient}"`,
          }).toString(),
        });
        return z.NEVER;
      }
      return { emails, domains };
    })
    .pipe(addSharingGrantsSchema);
}

function getFormSchema({
  canGrantDomains,
  canInviteExternal,
  t,
}: {
  canGrantDomains: boolean;
  canInviteExternal: boolean;
  t: (descriptor: MessageDescriptor) => string;
}) {
  return z.object({
    recipients: getRecipientsSchema(t).superRefine(({ domains }, ctx) => {
      if (!canGrantDomains && domains?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: canInviteExternal
            ? t(
                msg`Add individual email addresses. Domain sharing is not available.`
              )
            : t(msg`Only workspace member email addresses can be added.`),
        });
      }
    }),
  });
}

interface FrameSharingGrantsProps {
  sharing: SharingGrantsResponse | undefined;
  canInviteExternal: boolean;
  canRevoke: boolean;
  isLoading: boolean;
  hasError: boolean;
  onAdd: (
    recipients: z.infer<typeof addSharingGrantsSchema>
  ) => Promise<boolean>;
  onRevoke: (grantId: string) => Promise<boolean>;
  onRetry: () => void;
}

export function FrameSharingGrants({
  sharing,
  canInviteExternal,
  canRevoke,
  isLoading,
  hasError,
  onAdd,
  onRevoke,
  onRetry,
}: FrameSharingGrantsProps) {
  const { t } = useLingui();
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const canGrantDomains = sharing?.canGrantDomains ?? false;
  const formSchema = useMemo(
    () => getFormSchema({ canGrantDomains, canInviteExternal, t }),
    [canGrantDomains, canInviteExternal, t]
  );
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<z.input<typeof formSchema>, unknown, z.output<typeof formSchema>>(
    {
      resolver: zodResolver(formSchema),
      defaultValues: { recipients: "" },
      mode: "onSubmit",
      reValidateMode: "onSubmit",
    }
  );
  const { AwaitableDialog, showDialog } = useAwaitableDialog();
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const grants = [...(sharing?.accessGrants ?? [])].sort(
    (a, b) =>
      Number(!!a.blockedByPolicy) - Number(!!b.blockedByPolicy) ||
      compareStrings(a.target.value, b.target.value)
  );

  const revoke = async (grant: FileSharingGrantType) => {
    const label =
      grant.target.kind === "domain"
        ? `@${grant.target.value}`
        : grant.target.value;
    const confirmed = await showDialog({
      title: t`Remove access rule`,
      validateLabel: t`Remove`,
      validateVariant: "warning",
      cancelLabel: t`Cancel`,
      children: (
        <p>
          <Trans>
            Remove access for <strong>{label}</strong>? People may still have
            access through another invitation, domain or the frame’s general
            access setting.
          </Trans>
        </p>
      ),
    });
    if (!confirmed) {
      return;
    }
    setRevokingId(grant.sId);
    try {
      await onRevoke(grant.sId);
    } finally {
      setRevokingId(null);
    }
  };

  const inviteLabel = canGrantDomains
    ? t`Add people or domains`
    : canInviteExternal
      ? t`Invite by email`
      : t`Invite workspace members by email`;

  return (
    <div className="flex flex-col gap-4">
      <AwaitableDialog />
      <Section label={inviteLabel}>
        <form
          className="flex flex-col gap-2"
          onSubmit={handleSubmit(async ({ recipients }) => {
            const isAdded = await onAdd(recipients);
            if (isAdded) {
              reset();
            }
          })}
        >
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <Input
                id={inputId}
                aria-label={inviteLabel}
                placeholder={
                  canGrantDomains
                    ? "alice@example.com, example.com"
                    : t`Add comma separated emails`
                }
                {...register("recipients")}
                isError={!!errors.recipients}
                aria-invalid={!!errors.recipients}
                aria-describedby={errors.recipients ? errorId : undefined}
                disabled={isLoading || hasError || isSubmitting}
              />
            </div>
            <Button
              variant="primary"
              label={canGrantDomains ? t`Add` : t`Invite`}
              type="submit"
              isLoading={isSubmitting}
              disabled={isLoading || hasError}
            />
          </div>
          {errors.recipients && (
            <p
              id={errorId}
              role="alert"
              className="text-xs text-foreground px-2"
            >
              {errors.recipients.message}
            </p>
          )}
          {canGrantDomains && !errors.recipients && (
            <p className="text-xs text-muted-foreground px-2">
              <Trans>
                Invitations are sent to email addresses only. People at an added
                domain can open the link after verifying their email.
              </Trans>
            </p>
          )}
        </form>
      </Section>

      {isLoading ? (
        <div
          role="status"
          aria-label={t`Loading access rules`}
          className="flex justify-center py-4"
        >
          <div aria-hidden="true">
            <Spinner size="sm" />
          </div>
        </div>
      ) : hasError ? (
        <div role="alert" className="flex items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">
            <Trans>Could not load access rules.</Trans>
          </p>
          <Button label={t`Retry`} variant="outline" onClick={onRetry} />
        </div>
      ) : grants.length === 0 ? (
        <p className="px-2 text-sm text-muted-foreground">
          {canGrantDomains
            ? t`No people or domains have been added yet.`
            : t`No one has been invited yet.`}
        </p>
      ) : (
        <Section label={t`People with access`}>
          <ListGroup className="border-0">
            <ul>
              {grants.map((grant) => (
                <li key={grant.sId}>
                  <GrantRow
                    grant={grant}
                    isRevoking={revokingId === grant.sId}
                    onRevoke={
                      canRevoke && revokingId === null
                        ? () => revoke(grant)
                        : undefined
                    }
                  />
                </li>
              ))}
            </ul>
          </ListGroup>
        </Section>
      )}
    </div>
  );
}

interface GrantRowProps {
  grant: FileSharingGrantType;
  isRevoking: boolean;
  onRevoke?: () => void;
}

function GrantRow({ grant, isRevoking, onRevoke }: GrantRowProps) {
  const { t } = useLingui();
  const isDomain = grant.target.kind === "domain";
  const label = isDomain ? `@${grant.target.value}` : grant.target.value;
  const now = new Date();
  const grantedBy = grant.grantedBy?.fullName ?? grant.grantedBy?.email;
  const grantedAgo = formatTimeDistance(
    new Date(grant.grantedAt),
    now,
    undefined,
    getActiveLocale()
  );
  const grantedLabel = isDomain
    ? grantedBy
      ? t`Added by ${grantedBy} ${grantedAgo}`
      : t`Added ${grantedAgo}`
    : grantedBy
      ? t`Invited by ${grantedBy} ${grantedAgo}`
      : t`Invited ${grantedAgo}`;

  return (
    <FrameSharingRow
      label={label}
      icon={isDomain ? Globe01 : undefined}
      onRemove={onRevoke}
      isRemoving={isRevoking}
    >
      <span>{grantedLabel}</span>
      {grant.blockedByPolicy && (
        <span>
          {isDomain
            ? t`External viewers cannot access this frame.`
            : t`This invitation is restricted to workspace members.`}
        </span>
      )}
    </FrameSharingRow>
  );
}
